/**
 * EID-61: narrow reactive signatures for menu and panel reconciliation.
 *
 * These builders read exactly the properties the consuming effect renders /
 * reconciles on, and serialise them to a string. Evaluated inside a reactive
 * scope over store proxies, the property reads register fine-grained
 * subscriptions (deep ones for nested label/icon arrays via JSON.stringify),
 * while the string output gives the effect free equality gating — patches to
 * anything else under the same subtrees (e.g. a menu child node's content,
 * a panel's node spec) no longer re-run the effect.
 *
 * Kept dependency-free (no 'framework'/'utils' barrel imports) so it can be
 * unit tested per the repo testing note.
 */

export interface MenuLayoutEntry {
  /** Either a label or a [root, label] pair. */
  label?: unknown;
  /** Either an icon or a [rootIcon, labelIcon] pair. */
  icon?: unknown;
  color?: unknown;
}

/**
 * Signature of everything the menu tree is built from (see createMenu in
 * node.tsx): per-entry label/icon/color from the menu layout, plus the child
 * node ids in order. Child node *content* is deliberately excluded — the
 * rendered Node tracks its own spec reads.
 */
export const menuModelSignature = (
  menuLayout: MenuLayoutEntry[] | undefined | null,
  children: { id?: unknown }[] | undefined | null,
): string =>
  JSON.stringify({
    layout: (menuLayout ?? []).map((entry) => ({
      label: entry.label,
      icon: entry.icon,
      color: entry.color,
    })),
    ids: (children ?? []).map((child) => child.id),
  });

/**
 * Signature of the panel properties the spec-driven open/close reconciler
 * depends on: ids and open flags, in order. Panel content (node, title,
 * geometry) is excluded — it is rendered reactively elsewhere.
 */
export const panelsOpenSignature = (
  panels: { id?: unknown; open?: unknown }[] | undefined | null,
): string =>
  JSON.stringify((panels ?? []).map((panel) => [panel.id, !!panel.open]));
